import { useState, useEffect, createRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import moment from 'moment-timezone';

import Stack from '@mui/joy/Stack';
import Grid from '@mui/joy/Grid';
import Button from '@mui/joy/Button';
import IconButton from '@mui/joy/IconButton';
// import Link from '@mui/joy/Link';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Tooltip from '@mui/joy/Tooltip';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Textarea from '@mui/joy/Textarea';
import Chip from '@mui/joy/Chip';
import Select, { SelectOption } from '@mui/joy/Select';
import Option from '@mui/joy/Option';

import FullCalendar from '@fullcalendar/react'
import { EventSourceFuncArg, EventClickArg, EventChangeArg } from '@fullcalendar/core';

import dayGridWeek from '@fullcalendar/daygrid' // a plugin!
import timeGridWeek from '@fullcalendar/timegrid' // a plugin!
import listPlugin from '@fullcalendar/list';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';

import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction'; // needed for dayClick
import rrulePlugin from '@fullcalendar/rrule'

import Error from 'components/Error';
import Success from 'components/Success';
import LockedFeature from 'components/LockedFeature';
import { getIcon } from 'utils/misc';

import useWorkKey from 'hooks/useWorkKey';
import useCalendarEvent from 'hooks/useCalendarEvent';

import { CalendarEventType } from 'stores/calendar_event_store';
import { CalendarListItemType } from 'stores/calendar_auth_store';

const FAKE_PREFIX = 'FAKE://';
dayjs.extend(timezone);
dayjs.extend(utc);


/**
Fix the timezone issue with FullCalendar

This is copied from:
https://github.com/fullcalendar/fullcalendar/issues/5273#issuecomment-1012036833

MIT Licensed:    */
rrulePlugin.recurringTypes[0].expand = function (errd, fr, de) {
    return errd.rruleSet.between(
        fr.start,
        fr.end,
        true, // inclusive (will give extra events at start, see https://github.com/jakubroztocil/rrule/issues/84)
    ).map((date: Date) => new Date(de.createMarker(date).getTime() + date.getTimezoneOffset() * 60 * 1000))
}
/* end copied code */

export type CalendarToolsProps = {
    associated_type: string;
    associated_uuid: string;
    showChrome?: boolean;
    backTo?: string|null;
};

export type RenderableEventType = {
    id: string;
    calendar_id: string;
    title: string;
    start: string;
    end: string;
    allDay: boolean;

    description?: string;
    location?: string;
    backgroundColor?: string;
    textColor?: string;
    colorId?: string;
    borderColor?: string;
    isUnsaved: boolean;
    editable: boolean;
    rrule?: string;
};


function getRenderableEvent(event: CalendarEventType): RenderableEventType {
    // Convert CalendarEventType (from gcal) into RenderableEventType
    // for use with FullCalendar.

    // First convert the date.
    const start = event.start?.dateTime || event.start?.date || '';

    // Slightly reformat the rrule from google to fullcalendar.
    // 1) It's an array from google, so we join them into
    //    a string.
    // 2) We add the DTSTART line to the beginning which
    //    is requires by fullcalendar.
    const dtstart = moment(start).format('YYYYMMDDTHHmmss');
    const rrule = (
        event.recurrence?.length ?
            `DTSTART:${dtstart}\n${event.recurrence.join("\n")}` :
            undefined
    );


    const bg = event.calendar_list_entry?.backgroundColor; // CalendarColor(event.calendar_list_entry?.colorId || '1').classic;
    const fg = event.calendar_list_entry?.foregroundColor; // CalendarColor(event.calendar_list_entry?.colorId || '1').modern;

    // Return bag
    return {
        id: event.id,
        calendar_id: event.calendar?.id || '',
        title: event.summary,
        start: start,
        end: event.end?.dateTime || event.end?.date || '',
        allDay: event.start?.date ? true : false,
        rrule: rrule,
        backgroundColor: bg,
        textColor: fg,
        borderColor: event.editable ? undefined : "#bbbbbbaa",
        description: event.description || '',
        location: event.location || '',
        isUnsaved: false,
        editable: ['owner', 'writer'].includes(event.calendar_list_entry?.access_role || '')
    };
}

function getRenderableEvents(
    events:CalendarEventType[],
    associated_type:string,
    associated_uuid: string): RenderableEventType[] {
    return events
        .filter((e) => e.associated_type == associated_type && e.associated_uuid == associated_uuid)
        .map((e) => getRenderableEvent(e));
}

type CalEditorProps = {
    event:RenderableEventType,
    startStr: string,
    endStr: string,
    timezoneStr: string;
    associated_type: string,
    associated_uuid: string,
    calendarList: CalendarListItemType[],
    doCancel: () => void,
    onSaved: (event: CalendarEventType | null) => void,
    onDeleted: () => void
};

const CalendarOption = ({cal}: {cal: CalendarListItemType}) => {
    const CalendarIcon = getIcon('CalendarMonthOutlined');
    return (
        <Stack direction="row" spacing={1}>
            <CalendarIcon sx={{
                color: cal.backgroundColor,
                opacity: (cal.access_role == 'reader' ? 0.5 : 1),
            }} />
            <Typography
                sx={{
                opacity: (cal.access_role == 'reader' ? 0.5 : 1),
                }}
                >
                {cal.summaryOverride || cal.summary}
            </Typography>
        </Stack>
    );
};

const CalendarEventEditor = ({
    event,
    startStr, endStr,
    timezoneStr,
    associated_type, associated_uuid,
    calendarList,
    doCancel, onSaved, onDeleted }: CalEditorProps) => {

    const workKey = useWorkKey();
    const [title, setTitle] = useState(event.title);
    const [stateId, setStateId] = useState('');
    const [startTime, setStartTime] = useState<moment.Moment>(moment(startStr));
    const [endTime, setEndTime] = useState<moment.Moment>(moment(endStr));
    const [location, setLocation] = useState('');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [description, setDescription] = useState('');
    const { createEvent, updateEvent, deleteEvent } = useCalendarEvent();

    const CalendarIcon = getIcon('EditCalendar');
    const RightArrowIcon = getIcon('KeyboardArrowRight');
    const TimeIcon = getIcon('AccessTime');
        const TrashIcon = getIcon('DeleteOutlineOutlined');
        const TimezoneIcon = getIcon('Public');


    const [defaultCalendarId, setDefaultCalendarId] = useState(
        event.calendar_id ? event.calendar_id :
            (calendarList.filter(m => m.primary === true).length && calendarList.filter(m => m.primary === true)[0].id) || ''
    );

    const [calendarId, setCalendarId] = useState(defaultCalendarId);

    useEffect(() => {
        if (!calendarList.length) return;
        if (!defaultCalendarId && calendarList.filter(m => m.primary === true).length) {
            const id = calendarList.filter(m => m.primary === true)[0].id;
            setDefaultCalendarId(id);
            if (!calendarId) {
                setCalendarId(id);
            }
        }
    }, [calendarList]);

    useEffect(() => {
        // If it's a new event, set the title/description
        if(stateId !== event.id) {
            if(!event.id) {
                Error("Missing an id");
            }
            setStateId(event.id || '');
            setTitle(event.title || '');
            setDescription(event.description || '');
            setLocation(event.location || '');
        }

        // Always update the start/end times.

        setStartTime(moment(startStr));
        setEndTime(moment(endStr));
    }, [event, startStr, endStr]);


    function doDelete() {
        if(!workKey) {
            Error("Can't delete, unable to find identity.");
            return;
        }
        setDeleting(true);
        deleteEvent(workKey,
            associated_type,
            associated_uuid,
            event.id,
            event.calendar_id)
            .then(() => {
                Success("Deleted!");
                onDeleted && onDeleted();
            })
            .catch(() => {
                Error("Unable to delete event");
            })
            .finally(() => {
                setDeleting(false);
            });

    }

    function doSave() {

        // Coerce the data into the right format
        const data = {
            id: event.id,
            calendar: { id: calendarId },
            summary: title,
            description: description,
            start: { dateTime: startTime.format() },
            end: { dateTime: endTime.format() }
        } as CalendarEventType;

        // console.log("Data: ", data);
        // If if we're creating or updating it's pretty much the same,
        // so let's just set the function and message and then use the
        // same logic. Start by assuming we're updating.
        var func = updateEvent;
        var msg = "Event Updated!";
        // Are we creating this event instead?
        if(data.id.startsWith(FAKE_PREFIX)) {
            func = createEvent;
            msg = "Event Created!";
        }

        // console.log("going to create event:");
        setSaving(true);
        // Call update or create ByAssistant
        func(
            workKey,
            associated_type,
            associated_uuid,
            data
        )
            .then((event: CalendarEventType | null) => {
                if (event)
                    Success(msg);
                else
                    Error("There was an error");

                setSaving(false);
                // console.log("event: %o", event);
                onSaved(event);
            })
            .catch((err) => {
                setSaving(false);
                Error(err);
            });
    }

    return (
        <Stack spacing={3}>
            <FormControl>
                <FormLabel htmlFor="start">
                    <Typography
                        startDecorator={<CalendarIcon />}
                        fontSize="xl2"
                        level='title-lg'>
                        { startTime ? startTime.format('dddd MM/DD/YYYY') : `Start Time`}
                    </Typography>
                </FormLabel>

                    <Stack>
                        <Stack direction="row" spacing={1}>

                        <TimeIcon />
                        <Chip
                            size="lg"
                            onClick={() => console.log("Start time")}>
                            {startTime ? startTime.tz(timezoneStr).format('hh:mm a') : `Start Time`}
                        </Chip>

                        <RightArrowIcon />
                        <Chip
                            size="lg"
                            onClick={() => console.log("End time")}>
                            {endTime ? endTime.tz(timezoneStr).format('hh:mm a') : `End Time`}
                        </Chip>
                        </Stack>
                    </Stack>

                <Stack
                    direction="row"
                    justifyContent="space-between"
                >
                <Tooltip arrow
                    title="This person's timezone">
                <Typography
                    startDecorator={<TimezoneIcon />}
                    level="body-sm">
                    {timezoneStr}
                </Typography>
                </Tooltip>
                    {!event.id.startsWith('FAKE://') && event.editable &&
                        <Stack>
                            <LockedFeature
                                lockedIconName="DeleteOutlineOutlined"
                                copy="Delete">
                                <Stack alignItems="center" spacing={1}>
                                    <Typography
                                        level="title-md"
                                        color="danger">
                                        Are you sure?
                                    </Typography>
                                <Button
                                    variant="plain"
                                    color="danger"
                                    loading={deleting}
                                    startDecorator={<TrashIcon />}
                                    onClick={() => doDelete()}>
                                    Confirm Delete
                                </Button>
                                </Stack>
                            </LockedFeature>
                        </Stack>
                    }
            </Stack>

                {/*
                <TimePicker
                    label="Pick a time"
                    onChange={(e) => setStartTime(e)}
                    value={startTime}
                    />

                    */}
            </FormControl>


            { calendarList &&
                <FormControl>
                    <FormLabel htmlFor="calendar">Calendar</FormLabel>
                    <Select
                        onChange={(_, nv) => nv && event.editable && setCalendarId(nv)}
                        defaultValue={defaultCalendarId||''}
                        value={calendarId}
                        disabled={!event.isUnsaved}
                        renderValue={(selected: SelectOption<string> | null) => {
                            if(!selected) return null;
                            const c = calendarList.filter(m => m.id == selected.value)[0];
                            return <CalendarOption cal={c} />;
                        }}
                    >
                        {Object.entries(calendarList).map(([key, c]) => {
                            return (<Option
                                key={key}
                                value={c.id}
                                disabled={c.access_role == 'reader'}
                            >
                                <CalendarOption cal={c} />
                            </Option>);
                        }
                        )}

                    </Select>
                </FormControl>
            }

            <FormControl>
                <FormLabel htmlFor="title">Title</FormLabel>
                <Textarea
                    onChange={(e) => event.editable && setTitle(e.target.value)}
                    value={title}
                    id="title"
                    placeholder="Concise meeting title"
                />
            </FormControl>

            <FormControl>
                <FormLabel htmlFor="description">Description</FormLabel>
                <Textarea

                    minRows={3}
                    onChange={(e) => event.editable && setDescription(e.target.value)}
                    value={description}
                />
            </FormControl>

            <FormControl>
                <FormLabel htmlFor="location">Location</FormLabel>
                <Textarea
                    onChange={(e) => event.editable && setLocation(e.target.value)}
                    value={location}
                    id="location"
                    placeholder="Some address"
                />
            </FormControl>

            {/*
            <FormControl>
                <FormLabel htmlFor="end">End</FormLabel>
                <TimePicker
                    label="End Time"
                    onChange={(e) => setEndTime(e)}
                    value={endTime}
                />
            </FormControl>
              */}

            <Stack spacing={3}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                    spacing={1}>

                    <Button
                        fullWidth={!event.editable}
                        variant={event.editable ? "plain" : "outlined"}
                        onClick={() => doCancel()}>
                        Cancel
                    </Button>

                    {event.editable &&
                        <Button
                            loading={saving}
                            fullWidth
                            variant="outlined"
                            id="save-it"
                            color="primary"
                            onClick={doSave}>
                            Save
                        </Button>
                    }

                </Stack>

            </Stack>

        </Stack>
    );
};

export const CalendarFrame = observer(({
    associated_type,
    associated_uuid,
    // from_email,
    // associated_identity,
    // associated_user,
    showChrome = true,
    backTo = null
}: CalendarToolsProps) => {

    const workKey = useWorkKey();
    const navigate = useNavigate();

    const {
        fetchHasCalendar,
        fetchSyncedCalendars,
        fetchList,
        items
    } = useCalendarEvent();

    const [startStr, setStartStr] = useState('');
    const [endStr, setEndStr] = useState('');
    const [selectedEvent, setSelectedEvent] = useState<RenderableEventType | null>(null);
    const [events, setEvents] = useState([] as RenderableEventType[]);
    const [loading, setLoading] = useState(false);
    const [lastDates, setLastDates] = useState('');
    const [calendarPreferences, setCalendarPreferences] = useState('');
    const [timezoneStr, setTimezone] = useState<string|null>(null);
    const calendarRef = createRef<FullCalendar>();
    const LoadingIcon = getIcon('Replay');
    const HideIcon = getIcon('Close');
    const [noCalendar, setNoCalendar] = useState(false);
    const [calendarList, setCalendarList] = useState([] as CalendarListItemType[]);

    // Fetch the timezone and calendar preferences
    useEffect(() => {
        if(!workKey) {
            return;
        }
        fetchHasCalendar(
            workKey,
            associated_type,
            associated_uuid
        ).then((data) => {
            if(data) {
                setTimezone(data.timezone);
                setCalendarPreferences(data.calendar_preferences);
                // dayjs.tz.setDefault(data.timezone);
            }
            else {
                console.log("unable to set timezone");
            }
        })
            .catch((err) => {
                setNoCalendar(true);
            console.error("timezone error:", err);
        });
    }, [workKey,
        fetchHasCalendar,
        associated_uuid,
        associated_type]);


    // Fetch the synced ccalendar list
    useEffect(() => {
        if (!workKey) return;
        if (!(associated_type || associated_uuid)) return;
        if (!(startStr && endStr)) return;

        fetchSyncedCalendars(
            workKey,
            associated_type,
            associated_uuid)
            .then((data) => {
                setCalendarList(data);
            });
    }, [fetchSyncedCalendars,
        workKey,
        timezoneStr,
        startStr, endStr,
        associated_type, associated_uuid]);

    // Fetch the list of events
    useEffect(() => {
        if (!workKey) return;
        if (!startStr) return;
        if (!endStr) return;
        const k = startStr.split('T')[0] + endStr.split('T')[0];
        if (lastDates === `${k}`) return;

        setLastDates(k);
        setLoading(true);

        const s = moment(startStr).format('YYYY-MM-DDTHH:mm:ssZ');
        const e = moment(endStr).format('YYYY-MM-DDTHH:mm:ssZ');

        fetchList(
            workKey,
            associated_type,
            associated_uuid,
            s,
            e
        )
            .then((evts) => {
                refreshEvents(evts);
                setLoading(false);
            })
            .catch((err) => {
                Error("There was an error loading events.");
                console.error(err);
            })
            .finally(() => {
                setLoading(false);
            });

    }, [workKey, startStr, endStr]);

    /* We update the events `setEvents` when the store items
       (events) or the selectedEvent changes.
        */
    useEffect(() => {
        refreshEvents(items);
    }, [items.length, selectedEvent]);

    function refreshEvents(events: CalendarEventType[]) {
        console.log("Refreshing events: events: %o", events.slice());
        var evts = [] as RenderableEventType[];
        if (events.length > 0) {
            evts = getRenderableEvents(events, associated_type, associated_uuid);
        }
        if (selectedEvent && selectedEvent.id.startsWith(FAKE_PREFIX)) {
            evts = [...evts, selectedEvent];
        }
        console.log("Refreshing events: evts: %o", evts);
        setEvents(evts);
    }

    function handleDatesSet(info: EventSourceFuncArg) {
        if (info.startStr && info.endStr) {
            setStartStr(info.startStr);
            setEndStr(info.endStr);
        }
    }

    function changeView(view: string, date: string) {
        if(!timezoneStr) return;
        if (calendarRef.current) {
            calendarRef.current.getApi().changeView(view);
            calendarRef.current.getApi().gotoDate(date);
            var stt = moment(date);
            // Scroll to the time, with a bit of a buffer
            if (stt.hour() > 0) {
                stt = stt.subtract(15, 'minutes');
            }
            calendarRef.current.getApi().scrollToTime(stt.tz(timezoneStr).format('HH:mm:ss'));
        }
    }

    function handleDateClick(info: DateClickArg) {
        const fake_id = FAKE_PREFIX + Math.random()
            .toString(36).substring(7);
        const end = dayjs(info.dateStr).add(1, 'hour').format();

        const e = {
            id: fake_id,
            title: "",
            start: info.dateStr,
            end: end,
            editable: true,
            isUnsaved: true,
            backgroundColor: "#ff000022",
            borderColor: "#dd000044",
        } as RenderableEventType;

        setSelectedEvent(e);
        changeView('timeGridDay', info.dateStr);
    }

    function handleEventClick(info: EventClickArg) {
        const event = items.filter((m) => m.id == info.event.id)[0];
        if (event) {
            setSelectedEvent(getRenderableEvent(event));
        }
    }

    function handleEventChange(change: EventChangeArg) {
        const event = change.event;

        // If it's the one we're looking at, update it.
        if (event.id == selectedEvent?.id) {
            var s = Object.assign({}, selectedEvent) as RenderableEventType;
            s.start = event.startStr;
            s.end = event.endStr;
            setSelectedEvent(s);
        }
    }

    // We don't have a calendar, show the UI for that.
    if(noCalendar)
        return <NoCalendar />;


    if(!timezoneStr) {
        return (
            <Sheet>
                <Stack>
                    <Typography>
                        Loading...
                    </Typography>
                </Stack>
            </Sheet>
        );
    }

    return (
        <Sheet
            sx={{
                borderRadius: 'md',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                height: '100%',
                overflow: 'auto',
            }}
        >
            {(showChrome && backTo) &&
                <IconButton
                    sx={{
                        position: 'absolute',
                        top: 5,
                        right: 10,
                    }}
                    variant="plain"
                    onClick={() => navigate(backTo)}
                ><HideIcon /></IconButton>
            }

            <Stack
                pt={1}
                spacing={3}
            >
                        {loading &&
                            <Typography
                                endDecorator={
                                    <LoadingIcon
                                        sx={{
                                            animation: "backspin 1.4s linear infinite"
                                        }}
                                    />}
                                color="success">
                                Loading Events...
                            </Typography>
                        }

                {showChrome && !calendarPreferences &&
                    <Stack direction="row" spacing={1}>
                        <Stack>
                            <Typography level="title-lg">
                                Fancy Calendar
                            </Typography>
                            <Typography level="body-sm">
                                Let's plan together.
                            </Typography>
                        </Stack>
                    </Stack>
                }

                {calendarPreferences &&
                    <Stack>
                        <Typography level="title-md">
                            Notes from the user
                        </Typography>
                        <Typography>
                            {calendarPreferences}
                        </Typography>
                    </Stack>
                }

                <Grid container spacing={1} >
                    <Grid xs={12} sm={selectedEvent?.id ? 6 : 11}
                    >
                        <Sheet
                            sx={{
                                height: { xs: (selectedEvent?.id ? '40dvh' : '85dvh'), sm: '85dvh' },
                                // transitionDuration: '0.1s',
                                transitionProperty: "max-width",
                                width: "auto",
                                maxWidth: (selectedEvent?.id ? 800 : 1100),
                            }}
                         className="safari-only"
                        >
                            { timezoneStr &&
                            <FullCalendar
                                ref={calendarRef}
                                height="100%"
                                plugins={[dayGridWeek, timeGridWeek, listPlugin, interactionPlugin, rrulePlugin, momentTimezonePlugin]}

                                buttonText={{
                                    today: 'Today',
                                    month: 'Month',
                                    week: 'Week',
                                    day: 'Day',
                                    list: 'List'
                                }}

                                selectMirror={true}
                                initialView="timeGridWeek"
                                slotDuration="00:30:00"
                                slotLabelInterval="01:00:00"
                                scrollTime='10:00:00'
                                nowIndicator={true}
                                timeZone={timezoneStr}
                                timeZoneParam={timezoneStr}
                                events={
                                    events
                                }

                                businessHours={{
                                    daysOfWeek: [1, 2, 3, 4, 5], // M-F
                                    startTime: '8:00',
                                    endTime: '18:00',
                                }}
                                headerToolbar={{
                                    left: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
                                    center: 'title',
                                    right: 'prev,next today',
                                }}

                                datesSet={handleDatesSet}
                                eventChange={handleEventChange}
                                dateClick={handleDateClick}
                                eventClick={handleEventClick}
                            />
                            }
                { timezoneStr &&
                                <Stack
                                    padding={3}
                                    direction="row"
                                    justifyContent="center">
                                    <Typography level="body-sm">
                                        Timezone: {timezoneStr}
                                    </Typography>
                                </Stack>
                }
                        </Sheet>
                    </Grid>
                    <Grid xs={12}
                        sm={selectedEvent?.id ? 6 : 1}
                    >
                        <Stack m={1}>
                            {selectedEvent?.id &&
                                <CalendarEventEditor
                                    key={selectedEvent.id}
                                    calendarList={calendarList}
                                    event={selectedEvent}
                                    startStr={selectedEvent.start || ''}
                                    endStr={selectedEvent.end || ''}
                                    associated_type={associated_type}
                                    associated_uuid={associated_uuid}
                                    timezoneStr={timezoneStr}
                                    doCancel={
                                        () => {
                                            setSelectedEvent(null);
                                            // changeView('timeGridWeek', dayjs().format())
                                        }
                                    }
                                    onDeleted={
                                        () => {
                                            setSelectedEvent(null);
                                        }
                                    }
                                    onSaved={
                                        () => {
                                            Success("Saved");
                                            setSelectedEvent(null);
                                        }
                                    }
                                />
                            }
                        </Stack>
                    </Grid>
                </Grid>
            </Stack>
        </Sheet>

    );

});

const NoCalendar = ({showButton=true}: {showButton?: boolean}) => {
    const EditCalendar = getIcon('EditCalendar');
    const navigate = useNavigate();
    return (
        <Stack direction="row"
            justifyContent="center">
            <Typography
                startDecorator={<EditCalendar />}
                level="body-xs"
            >
                No calendar found
            </Typography>
            {showButton &&
                <Button
                    onClick={() => navigate('/calendar/settings')}
                >Setup your Calendar</Button>
            }
        </Stack>
    );
};

const CalendarTools = observer(({
    associated_type,
    associated_uuid,
    // from_email,
    // associated_identity,
    // associated_user
}: CalendarToolsProps) => {
    const navigate = useNavigate();
    const workKey = useWorkKey();
    const EditCalendar = getIcon('EditCalendar');
    const [loadingCalendar, setLoadingCalendar] = useState(false);
    const [noCalendar, setNoCalendar] = useState(false);
    const { fetchHasCalendar } = useCalendarEvent();
    //    const [showCalendarDrawer, setCalendarDrawer] = useState(false);
    const url = `/${associated_type}/${associated_uuid}/calendar`;

    useEffect(() => {
        if (!workKey) return;
        setLoadingCalendar(true);

        fetchHasCalendar(
            workKey,
            associated_type,
            associated_uuid
        )
            .then(() => {
                setNoCalendar(false);
            })
            .catch(() => {
                setNoCalendar(true);
            })
            .finally(() => {
                setLoadingCalendar(false);
            });

    }, [associated_type, associated_uuid]);


    if(noCalendar)
        return <NoCalendar showButton={false} />;


    return (
        <>
            <Stack
                px={1}
                spacing={1}>
                <Tooltip
                    placement="left"
                    arrow title="Open the Calendar Tools">
                    <Button
                        component="button"
                        size="sm"
                        variant="plain"
                        loading={loadingCalendar}
                        startDecorator={<EditCalendar />}
                        onClick={() => navigate(url)}
                    >
                        Calendar
                    </Button>
                </Tooltip>
            </Stack>
        </>
    );
});
export default CalendarTools;
